import type {DynamicLabelExtra as DynamicLabel} from 'src/types/dynamic-labels';
import type {JobVariable} from 'src/types/chatbot';

import type {EntityType} from 'src/types/ats-entities';
import type {
  CustomButtonContextValueType,
  CustomLinkButtonContextValueType,
} from './types';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/RawDraftContentState'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/RawDraftContentState.js' implicitly has an 'any' type.
import type {RawDraftContentState} from 'draft-js/lib/RawDraftContentState';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/DraftDecoratorType'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/DraftDecoratorType.js' implicitly has an 'any' type.
import type {DraftDecoratorType} from 'draft-js/lib/DraftDecoratorType';

import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {Toggle} from '@spaced-out/ui-design-system/lib/components/Toggle';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {usePointerDrag} from 'src/hooks/usePointerDrag';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import isEmpty from 'lodash/isEmpty';

import {
  Editor,
  EditorState,
  RichUtils,
  CompositeDecorator,
  KeyBindingUtil,
  convertToRaw,
  convertFromRaw,
  SelectionState,
  Modifier,
  ContentBlock,
  ContentState,
  ContentState as ContentStateAPI,
  genKey,
  DraftEditorCommand,
  RawDraftContentBlock,
} from 'draft-js';

import {pushModal} from 'src/action-creators/modal';

import {
  templateVariableDecorator,
  nodeVariableDecorator,
  hyperlinkDecorator,
  prepareForServer,
  GifBlock,
} from 'src/components/conversation-builder/draft-editor';
import {LinkDecoratorEntry} from './draft-link';

import {PlaceholderDecorator} from './draft-placeholder';

import {
  AttachmentLinkDecorator,
  dynamicLabelDecorator,
  addTemplateVariable,
  addJobResultVariable,
  addChatbotLink,
  addHyperlink,
  addGif,
  addDynamicLabel,
} from './dynamic-text';

import {classify} from 'src/utils';
import {insertText} from 'src/utils/draft';

import EmojiDialog from 'src/components/lib/emoji-picker/dialog';
import GifDialog from 'src/components/lib/gif-picker/dialog';

import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Menu} from '@spaced-out/ui-design-system/lib/components/Menu';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import {
  SubTitleLarge,
  SubTitleMedium,
  SubTitleSmall,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import type {ToastSemanticType} from '@spaced-out/ui-design-system/lib/components/Toast';

import css from './genesis.css';
import 'node_modules/draft-js/dist/Draft.css';
import './draft.global.css';


export type DynamicLabels = DynamicLabel[];
export const CustomButtonContext: React.Context<CustomButtonContextValueType | null> =
  React.createContext<CustomButtonContextValueType | null>(null);
export const CustomLinkButtonContext: React.Context<CustomButtonContextValueType | null> =
  React.createContext<CustomLinkButtonContextValueType | null>(null);
type AttachmentLinkMeta = {
  pattern: string;
  label: string;
};
type VariablePreviewValue = {
  [key: string]: string;
};

type ClassNames = Readonly<{
  wrapper?: string;
  editor?: string;
}>;

export type LinkV2 = {
  url: string;
  label: string;
  metadata?: {
    [key: string]: string;
  };
};

export type RichEditorProps = {
  onChange: (rawDraftContentState: RawDraftContentState) => unknown;
  onBlur?: () => void;
  defaultValue: RawDraftContentState;
  readOnly?: boolean;
  variableLists?: {
    type: 'node' | 'ats' | 'template';
    variables: MenuOption[];
  }[];
  templateVariables?: MenuOption[];
  nodeVariables?: MenuOption[];
  atsVariables?: DynamicLabels;
  entityType?: EntityType;
  jobResultVariables?: JobVariable[];
  includeFormatting?: boolean;
  includeChatbotLink?: boolean;
  includeEmoji?: boolean;
  editorHeight?: string;
  includeFileAttachement?: boolean;
  includeHyperlink?: boolean;
  includeHyperlinkV2?: boolean;
  chatbotCustomLinkGenerationEnabled?: boolean;
  handleSaveFile?: (
    arg1: File,
    arg2: () => unknown,
    arg3: () => unknown,
  ) => unknown;
  files?: {
    name: string;
    handleDownload: () => unknown;
    handleRemove?: () => unknown;
  }[];
  isOneLine?: boolean;
  includeGif?: boolean;
  expandToFit?: boolean;
  placeholder?: string;
  atsTooltip?: string;
  prepareContentOnLoad?: (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) => EditorState;
  isGenesis?: boolean;
  classNames?: ClassNames;
  enableDraftJSONCopy?: boolean;
};

export default function RichEditor({
  // dynamicLabels,
  onChange,

  onBlur,
  defaultValue,
  readOnly = false,
  variableLists = [],
  includeFormatting = true,
  editorHeight = '200px',
  includeFileAttachement = false,
  handleSaveFile,
  files,
  isOneLine,
  includeHyperlink,
  includeHyperlinkV2 = false,
  chatbotCustomLinkGenerationEnabled = false,
  templateVariables,
  nodeVariables,
  atsVariables,
  jobResultVariables,
  entityType,
  includeGif = false,
  prepareContentOnLoad,
  expandToFit = false,
  includeChatbotLink = false,
  includeEmoji = true,
  placeholder,
  isGenesis = false,
  classNames,
  atsTooltip = 'ATS variables',
  enableDraftJSONCopy = false,
  ...props
}: RichEditorProps) {
  const {t, makeKey} = useLabelTranslation();

  const dispatch = useDispatch(); //TODO: when moving to genesis, this will need to be passed in instead of being declared here
  const [showShroud, setShowShroud] = React.useState(false);
  const [attachingFile, setAttachingFile] = React.useState(false);
  const [addingLink, setAddinglink] = React.useState(false);
  const [addingLinkV2, setAddinglinkV2] = React.useState(false);
  const [attachChatbotCustomLink, setAttachChatbotCustomLink] =
    React.useState(false);
  const [_, copy] = useCopyToClipboard();
  const [errorMsg, setErrorMsg] = React.useState('');

  // @ts-expect-error - TS2339 - Property 'env' does not exist on type 'DefaultRootState'.
  const BASE_API_ENDPOINT: string = useSelector((state) => state.env.apiUrl);

  const handleRemoveLink = (
    blockKey: undefined,
    start: undefined,
    end: undefined,
    label: undefined,
  ) => {
    const currentEditorState = getEditorState(); //to get current editor state and not just the snapshot saved on mount
    let content = currentEditorState.getCurrentContent();

    const targetRange = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: start,
      focusKey: blockKey,
      focusOffset: end,
    });

    content = Modifier.applyEntity(content, targetRange, null);
    content = Modifier.replaceText(
      content,
      targetRange,
      String(label),
      // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'DraftInlineStyle | undefined'.
      null,
      null,
    );

    const newState = EditorState.push(
      currentEditorState,
      content,
      'apply-entity',
    );

    const newSelection = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: 0,
      focusKey: blockKey,
      focusOffset: 0,
    });
    handleChange(EditorState.forceSelection(newState, newSelection));
  };

  const handleEditLink = (
    blockKey: undefined,
    start: undefined,
    end: undefined,
    url: undefined,
    label: undefined,
    metadata: undefined,
  ) => {
    //this essentially removes the old link and places the selection in the correct place
    //so we could just save a brand new copy once user clicks save on the modal

    const currentEditorState = getEditorState();
    let content = currentEditorState.getCurrentContent();

    // Store backup of current state before modifications
    setBackupState({
      blockKey,
      start,
      end,
      content,
      url,
      label,
      metadata,
    });

    const targetRange = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: start,
      focusKey: blockKey,
      focusOffset: end,
    });

    content = Modifier.applyEntity(content, targetRange, null);
    // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'DraftInlineStyle | undefined'.
    content = Modifier.replaceText(content, targetRange, '', null, null);

    const newState = EditorState.push(
      currentEditorState,
      content,
      'apply-entity',
    );

    const newSelection = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: start,
      focusKey: blockKey,
      focusOffset: end,
    });
    handleChange(EditorState.forceSelection(newState, newSelection));

    if (includeHyperlinkV2) {
      handleSetLinkV2(url, label, metadata ? true : false);
      setAddinglinkV2(true);
      setAttachChatbotCustomLink(metadata ? true : false);
    } else {
      setLink({
        url,
        label,
      });
      setAddinglink(true);
      setShowShroud(true);
    }
  };

  // Add new function to restore from backup
  const handleResetLink = () => {
    if (backupState) {
      const {blockKey, start, end, content} = backupState;

      const targetRange = new SelectionState({
        anchorKey: blockKey,
        anchorOffset: start,
        focusKey: blockKey,
        focusOffset: end,
      });

      // Restore the original entity and text
      const newContent = content;
      const currentState = getEditorState();
      const newState = EditorState.push(
        currentState,
        newContent,
        'apply-entity',
      );

      handleChange(EditorState.forceSelection(newState, targetRange));
      setBackupState(null);
    }

    // Reset UI state
    setAddinglinkV2(false);
    setAddinglink(false);
    setShowShroud(false);
    setAttachChatbotCustomLink(false);
  };

  const decorator = buildDecorator({
    dynamicLabels: atsVariables,
    linkTarget: '_blank',
    onPlaceholderReplace: () => alert('on placeholder replace'),
    // @ts-expect-error - TS2322 - Type '(blockKey: undefined, start: undefined, end: undefined, label: undefined) => void' is not assignable to type '() => void'.
    handleRemoveLink,
    // @ts-expect-error - TS2322 - Type '(blockKey: undefined, start: undefined, end: undefined, url: undefined, label: undefined, metadata: undefined) => void' is not assignable to type '() => void'.
    handleEditLink,
    chatbotCustomLinkGenerationEnabled,
  });

  const [editorState, setEditorState] = React.useState(() => {
    if (defaultValue) {
      const upgradedContent = prepareContentOnLoad
        ? prepareContentOnLoad(defaultValue, decorator).getCurrentContent()
        : convertFromRaw(defaultValue);

      onChange?.(convertToRaw(upgradedContent));

      return EditorState.createWithContent(upgradedContent, decorator);
    } else {
      return EditorState.createEmpty(decorator);
    }
  });
  // backup state useful for clear functionality
  const [backupState, setBackupState] = React.useState<any>(null);
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const rootRef = React.useRef();

  const editorStateRef = React.useRef<EditorState>(editorState);
  editorStateRef.current = editorState;
  const getEditorState = React.useCallback((): EditorState => {
    return editorStateRef.current;
  }, []);

  // @ts-expect-error - TS7006 - Parameter 'newEditorState' implicitly has an 'any' type.
  const handleChange = (newEditorState) => {
    setEditorState(newEditorState);
    const currentContent = newEditorState.getCurrentContent();

    if (onChange) {
      onChange(convertToRaw(currentContent));
    }
  };

  const handleGifButtonClick = () => {
    dispatch(
      pushModal({
        render: ({removeModal}) => (
          <GifDialog
            onClose={removeModal}
            onSelect={(gif) => {
              handleChange(
                // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'GifData'.
                addGif(editorState, editorState.getSelection(), gif),
              );
            }}
          />
        ),
      }),
    );
  };

  const handleEmojiButtonClick = () => {
    dispatch(
      pushModal({
        render: ({removeModal}) => (
          <EmojiDialog
            onClose={removeModal}
            onSelect={(emoji) => {
              handleChange(insertText(editorState, emoji));
            }}
            t={t}
            makeKey={makeKey}
          />
        ),
      }),
    );
  };

  const handleSelectNodeVariable = (selected: MenuOption) => {
    const {key, label, ...rest} = selected;
    const nodeVariable = selected ? {...rest} : null;
    if (nodeVariable) {
      const result = addDynamicLabel(
        editorState,
        editorState.getSelection(),
        nodeVariable,
        'NODE_VARIABLE',
      );
      setEditorState(result);

      onChange(prepareForServer(result, atsVariables ?? []));
    }
  };

  const handleSelectAtsVariable = () => {
    if (atsVariables) {
      dispatch(
        pushModal({
          type: 'VARIABLE_PICKER',
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'labels' does not exist in type 'BasicModal'.
          labels: atsVariables,
          // @ts-expect-error - TS7006 - Parameter 'dynamicLabel' implicitly has an 'any' type.
          insertResult: (dynamicLabel) => {
            handleChange(
              addDynamicLabel(
                editorState,
                editorState.getSelection(),
                dynamicLabel,
              ),
            );
          },
          entityType,
        }),
      );
    }
  };

  const handleSelectTemplateVariable = (selected: MenuOption) => {
    handleChange(
      addTemplateVariable(editorState, editorState.getSelection(), {
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'value' does not exist in type 'GifData'.
        value: selected.key,
        display_name: selected.label,
      }),
    );
  };

  const handleSelectJobVariable = (selected: MenuOption) => {
    handleChange(
      addJobResultVariable(editorState, editorState.getSelection(), {
        value: selected.key,
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        display_name: selected.label,
      }),
    );
  };

  const handleAddChatbotLink = () => {
    handleChange(addChatbotLink(editorState, editorState.getSelection()));
  };

  const handleAddHyperlink = () => {
    if (link) {
      handleChange(
        addHyperlink(editorState, editorState.getSelection(), {
          ...link,
          onEdit: handleEditLink,
          onRemove: handleRemoveLink,
        }),
      );
    }
    toggleShowAddHyperlink();
  };

  const handleAddHyperlinkV2 = () => {
    if (linkV2) {
      handleChange(
        addHyperlink(editorState, editorState.getSelection(), {
          ...linkV2,
          // @ts-expect-error - TS2322 - Type '(blockKey: undefined, start: undefined, end: undefined, url: undefined, label: undefined, metadata: undefined) => void' is not assignable to type '() => void'.
          onEdit: handleEditLink,
          // @ts-expect-error - TS2322 - Type '(blockKey: undefined, start: undefined, end: undefined, label: undefined) => void' is not assignable to type '() => void'.
          onRemove: handleRemoveLink,
        }),
      );
    }
    toggleShowAddHyperlinkV2();
  };

  const [height, setHeight] = React.useState(editorHeight);
  const [resizeHandleProps] = usePointerDrag({
    onMove: ({deltaY}) => {
      const {current} = rootRef;
      if (current) {
        // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
        setHeight(current.offsetHeight + deltaY);
      }
    },
  });
  React.useEffect(() => {
    if (!expandToFit) {
      // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
      setHeight(editorHeight || rootRef.current?.offsetHeight || 0);
    }
  }, []);

  React.useEffect(() => {
    if (expandToFit) {
      const {current} = rootRef;
      if (current) {
        // setHeight(current.offsetHeight);
        // @ts-expect-error - TS2339 - Property 'scrollHeight' does not exist on type '{}'.
        setHeight(current.scrollHeight);
      }
    }
  }, [editorState]);

  // File Upload
  const [file, setFile] = React.useState<any>();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const fileRef = React.useRef();
  const toggleShowAttachFile = () => {
    setShowShroud(!showShroud);
    setAttachingFile(!attachingFile);

    if (attachingFile && file) {
      setFile(null);
    }
  };

  // Hyperlink
  const [link, setLink] = React.useState<any>();
  const [linkV2, setLinkV2] = React.useState<LinkV2>({
    url: '',
    label: '',
  });

  const toggleAttachChatbotCustomLink = () => {
    handleSetLinkV2(linkV2.url, linkV2.label, !attachChatbotCustomLink);
    setAttachChatbotCustomLink(!attachChatbotCustomLink);
  };

  const handleSetLinkV2 = (
    url: string | null | undefined = '',
    label: string | null | undefined = '',
    attachMetaData: boolean,
  ) => {
    const linkPayload: LinkV2 = {
      // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
      url,
      // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
      label,
    };

    if (attachMetaData) {
      linkPayload.metadata = {
        conv_id: 'conversation_id',
      };
    }

    setLinkV2(linkPayload);
  };

  const toggleShowAddHyperlink = () => {
    setShowShroud(!showShroud);
    setAddinglink(!addingLink);

    if (addingLink && link) {
      setLink(null);
    } else {
      handleUseSelectedText();
    }
  };

  const toggleShowAddHyperlinkV2 = () => {
    setAddinglinkV2(!addingLinkV2);

    if (addingLinkV2 && linkV2) {
      setLinkV2({
        url: '',
        label: '',
      });
    } else {
      handleUseSelectedText();
    }
  };

  const handleCompleteSaveFile = () => {
    if (handleSaveFile && file) {
      handleSaveFile(file, toggleShowAttachFile, () =>
        console.log('error saving file'),
      );
    }
  };

  const handleUseSelectedText = () => {
    const currentContent = editorState.getCurrentContent();

    const selectionState = editorState.getSelection();
    const anchorKey = selectionState.getAnchorKey();
    const currentContentBlock = currentContent.getBlockForKey(anchorKey);

    const start = selectionState.getStartOffset();
    const end = selectionState.getEndOffset();
    const selectedText = currentContentBlock.getText().slice(start, end);

    if (selectedText) {
      if (includeHyperlinkV2) {
        setLinkV2({
          url: '',
          label: selectedText,
        });
      } else {
        setLink({
          url: '',
          label: selectedText,
        });
      }
    }
  };

  const handleTab = (e: React.KeyboardEvent) => {
    handleChange(RichUtils.onTab(e, editorState, 4)); // 4 is max depth
  };

  const handleKeyCommand = (command: DraftEditorCommand | string) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return true;
    }
    return false;
  };

  const showToastMessage = (message: string, type: ToastSemanticType) => {
    const toastJsx = (
      <Toast semantic={type}>
        <ToastTitle>{message} </ToastTitle>
      </Toast>
    );
    toastApi.show(toastJsx, {
      autoClose: true,
    });
  };

  const handleCopyAPIEndpoint = () => {
    const url = new URL(BASE_API_ENDPOINT);
    // Split hostname by dots and replace first part with 'business'
    const hostnameParts = url.hostname.split('.');
    hostnameParts[0] = 'business';
    url.hostname = hostnameParts.join('.');
    const APIEndpoint = `${url.toString()}api/v1/chatbot/conversation/{conv_id}/data`;

    copy(APIEndpoint)
      .then((success) => {
        if (success) {
          showToastMessage(
            'API endpoint copied to clipboard.',
            TOAST_SEMANTIC.success,
          );
        } else {
          showToastMessage('Something went wrong', TOAST_SEMANTIC.danger);
        }
      })
      .catch((_error) => {
        showToastMessage('Something went wrong', TOAST_SEMANTIC.danger);
      });
  };

  // Utility to encode JSON as HTML
  const jsonToHtml = (jsonString: string) => {
    const encodedJson = btoa(unescape(encodeURIComponent(jsonString)));
    return `<p data-json="${encodedJson}"></p>`;
  };

  // Utility to decode JSON from HTML
  const htmlToJsonString = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const encodedJson = doc.querySelector('p')?.getAttribute('data-json');
    if (encodedJson) {
      const jsonString = decodeURIComponent(escape(atob(encodedJson)));
      return jsonString;
    }
    return '';
  };

  const handleCopy = () => {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      return;
    }

    const rawContent = convertToRaw(editorState.getCurrentContent()),
      startKey = selection.getStartKey(),
      endKey = selection.getEndKey(),
      startOffset = selection.getStartOffset(),
      endOffset = selection.getEndOffset(),
      // @ts-expect-error - TS7034 - Variable 'selectedBlocks' implicitly has type 'any[]' in some locations where its type cannot be determined.
      selectedBlocks = [],
      selectedEntityMap: Record<string, any> = {};

    rawContent.blocks.forEach((block) => {
      // Only process blocks within the selection range
      if (
        block.key === startKey ||
        block.key === endKey ||
        (startKey !== endKey &&
          rawContent.blocks.findIndex((b) => b.key === startKey) <
            rawContent.blocks.findIndex((b) => b.key === block.key) &&
          rawContent.blocks.findIndex((b) => b.key === block.key) <
            rawContent.blocks.findIndex((b) => b.key === endKey))
      ) {
        const blockText = block.text;
        const entityRanges = block.entityRanges || [];
        const updatedEntityRanges: Array<{
          key: string;
          length: any | number;
          offset: number;
        }> = [];
        const processedEntities = new Set();

        // Determine text boundaries for this block
        const copyStart = block.key === startKey ? startOffset : 0;
        const copyEnd = block.key === endKey ? endOffset : blockText.length;
        let finalText = '';
        let hasEntities = false;

        // Process entities that overlap with the selection
        let currentPosition = copyStart;
        entityRanges.forEach((entityRange) => {
          const entityKey = String(entityRange.key);
          const entity = rawContent.entityMap[entityKey];

          // entity not found in raw entity map
          // todo(AMAN): see if this check is even needed
          if (!entity) {
            return;
          }

          const entityStart = entityRange.offset;
          const entityEnd = entityRange.offset + entityRange.length;

          // Check if entity overlaps with selection
          const hasOverlap =
            // entity starts in selection
            (entityStart >= copyStart && entityStart < copyEnd) ||
            // entity ends in selection
            (entityEnd > copyStart && entityEnd <= copyEnd) ||
            // entity contains selection
            (entityStart <= copyStart && entityEnd >= copyEnd);

          if (hasOverlap) {
            hasEntities = true;

            // Add any text before this entity
            if (entityStart > currentPosition) {
              const textBefore = blockText.slice(currentPosition, entityStart);
              finalText = finalText ? `${finalText}${textBefore}` : textBefore;
            }

            // Use the exact text from the entity's name field in data
            let entityText = entity.data?.name || '';

            // Wrap DYNAMIC_LABEL entities in <> if not already wrapped
            if (
              entity.type === 'DYNAMIC_LABEL' &&
              !entityText.match(/^<.*>$/)
            ) {
              entityText = `<${entityText}>`;
            }

            finalText = finalText ? `${finalText}${entityText}` : entityText;

            // Update current position
            currentPosition = entityEnd;

            // Add entity to map if not already processed
            if (!processedEntities.has(entityKey)) {
              selectedEntityMap[entityKey] = entity;
              processedEntities.add(entityKey);
            }

            // Update entity range with new offset based on final text position
            updatedEntityRanges.push({
              offset: finalText.length - entityText.length,
              length: entityText.length,
              key: entityKey,
            });
          }
        });

        // Add any remaining text after the last entity
        if (currentPosition < copyEnd) {
          const textAfter = blockText.slice(currentPosition, copyEnd);
          finalText = finalText ? `${finalText}${textAfter}` : textAfter;
        }

        // If no entities were found in selection, use the plain selected text
        if (!hasEntities) {
          finalText = blockText.slice(copyStart, copyEnd);
        }

        // Create the block with updated text and entity ranges
        selectedBlocks.push({
          ...block,
          text: finalText,
          entityRanges: updatedEntityRanges,
        });
      }
    });

    const copiedContent = {
      // @ts-expect-error - TS7005 - Variable 'selectedBlocks' implicitly has an 'any[]' type.
      blocks: selectedBlocks,
      entityMap: selectedEntityMap,
    } as const;

    // Store in clipboard with both JSON and plain text
    const jsonString = JSON.stringify(copiedContent);
    const plainText = copiedContent.blocks
      .map((block) => block.text)
      .join('\n');

    // Create clipboard data with both formats
    const clipboardData = new ClipboardItem({
      'text/plain': plainText,
      'text/html': jsonToHtml(jsonString),
    });

    // Write to clipboard
    navigator.clipboard
      .write([clipboardData])
      .catch((err) => console.error('Failed to copy:', err));

    return copiedContent;
  };

  // Event handler for detecting key presses (Ctrl+C or Cmd+C)
  const handleKeyDown = (e: any) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      handleCopy();
    }
  };

  const isValidJSON = (input: string): boolean => {
    try {
      JSON.parse(input);
      return true;
    } catch (err: any) {
      return false;
    }
  };

  // returns if all entities in entity map are valid ATS or Node variable
  const areEntitiesValid = (
    entityMap: any,
    nodeVariables = [],
    atsVariables = [],
  ) => {
    const keys = Object.keys(entityMap);
    for (const key of keys) {
      const {type, data} = entityMap[key];
      // ats or external variable
      if (type === 'DYNAMIC_LABEL') {
        const found = atsVariables.find(
          // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'never'.
          (atsVariable) => atsVariable.value === data.value,
        );
        if (!found) {
          return false;
        }
      }
      // node variable
      else {
        const found = nodeVariables.find(
          // @ts-expect-error - TS2339 - Property 'node_name' does not exist on type 'never'.
          (nodeVariable) => nodeVariable.node_name === data.node_name,
        );
        if (!found) {
          return false;
        }
      }
    }
    return true;
  };

  const handlePaste = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    // @ts-expect-error - TS2551 - Property 'clipboardData' does not exist on type 'Window & typeof globalThis'. Did you mean 'Clipboard'?
    const clipboardData = event.clipboardData || window.clipboardData;
    let pastedText;
    const pastedMarkup = clipboardData.getData('text/html');

    if (pastedMarkup) {
      pastedText = htmlToJsonString(pastedMarkup);
    } else {
      pastedText = clipboardData.getData('text/plain');
    }

    // @ts-expect-error - TS7034 - Variable 'pastedContent' implicitly has type 'any' in some locations where its type cannot be determined.
    let pastedContent;
    try {
      if (isValidJSON(pastedText)) {
        pastedContent = JSON.parse(pastedText);
        if (!pastedContent?.blocks || !pastedContent?.entityMap) {
          throw new Error('Pasted content does not seem correct');
        }
        if (
          !areEntitiesValid(
            pastedContent.entityMap,
            // @ts-expect-error - TS2345 - Argument of type 'MenuOption[] | undefined' is not assignable to parameter of type 'never[] | undefined'.
            nodeVariables,
            atsVariables,
          )
        ) {
          setErrorMsg(
            'Some variables used in this field are not supported. Please use the variable picker to select and add supported variables.',
          );
        }
      } else {
        // Create a simple draft-js content state for plain text
        pastedContent = {
          blocks: [
            {
              key: genKey(),
              text: pastedText,
              type: 'unstyled',
              depth: 0,
              inlineStyleRanges: [],
              entityRanges: [],
              data: {},
            },
          ],
          entityMap: {},
        };
      }

      const rawTargetContent = convertToRaw(editorState.getCurrentContent());

      // Get current selection and block info
      const selection = editorState.getSelection();
      const currentBlockKey = selection.getStartKey();
      const currentOffset = selection.getStartOffset();

      // Create a mapping of old entity keys to new keys
      const newEntityMap = {...rawTargetContent.entityMap} as const;
      // To remap entity keys
      const entityKeyMapping: Record<string, any> = {};

      // adding entities from pasted content to the destination entity map
      // todo(AMAN): there is a scope for entity duplication here. Need to fix that
      Object.keys(pastedContent.entityMap).forEach((oldKey) => {
        const newKey = Object.keys(newEntityMap).length.toString();
        // @ts-expect-error - TS2542 - Index signature in type '{ readonly [key: string]: RawDraftEntity<{ [key: string]: any; }>; }' only permits reading. | TS7005 - Variable 'pastedContent' implicitly has an 'any' type.
        newEntityMap[newKey] = pastedContent.entityMap[oldKey];
        entityKeyMapping[oldKey] = newKey;
      });

      // Find the current block
      const currentBlock = rawTargetContent.blocks.find(
        (block) => block.key === currentBlockKey,
      );

      // Split current block text and entities
      const textBeforeCursor = currentBlock?.text.slice(0, currentOffset);
      const textAfterCursor = currentBlock?.text.slice(currentOffset);

      // Handle entity ranges for the split blocks
      const beforeEntityRanges = currentBlock?.entityRanges?.filter(
        // @ts-expect-error TS2304
        (range: EntityRange) => range.offset + range.length <= currentOffset,
      );

      const afterEntityRanges = currentBlock?.entityRanges
        // @ts-expect-error TS2304
        ?.filter((range: EntityRange) => range.offset >= currentOffset)
        // @ts-expect-error TS2304
        .map((range: EntityRange) => ({
          ...range,
          offset: range.offset - currentOffset,
        }));

      // Create blocks array with split current block and pasted blocks
      const updatedBlocks: Array<RawDraftContentBlock> = [];

      // Add blocks before current block
      rawTargetContent.blocks.forEach((block) => {
        if (block.key === currentBlockKey) {
          // Add first part of split block if it has content
          if (textBeforeCursor) {
            updatedBlocks.push({
              ...currentBlock,
              text: textBeforeCursor,
              // @ts-expect-error TS2322
              entityRanges: beforeEntityRanges,
            });
          }

          // Add pasted blocks with remapped entity keys
          // @ts-expect-error - TS7005 - Variable 'pastedContent' implicitly has an 'any' type. | TS7006 - Parameter 'pastedBlock' implicitly has an 'any' type.
          pastedContent.blocks.forEach((pastedBlock) => {
            updatedBlocks.push({
              ...pastedBlock,
              key: genKey(),
              // @ts-expect-error - TS7006 - Parameter 'range' implicitly has an 'any' type.
              entityRanges: pastedBlock.entityRanges.map((range) => ({
                ...range,
                key: entityKeyMapping[range.key],
              })),
            });
          });

          // Add second part of split block if it has content
          if (textAfterCursor) {
            updatedBlocks.push({
              ...currentBlock,
              key: genKey(),
              text: textAfterCursor,
              // @ts-expect-error TS2322
              entityRanges: afterEntityRanges,
            });
          }
        } else if (!updatedBlocks.find((b) => b.key === block.key)) {
          updatedBlocks.push(block);
        }
      });

      const newContentConfig = {
        blocks: updatedBlocks,
        entityMap: newEntityMap,
      } as const;

      const newContentState = prepareContentOnLoad
        ? prepareContentOnLoad(newContentConfig, decorator).getCurrentContent()
        : convertFromRaw(newContentConfig);

      onChange?.(convertToRaw(newContentState));

      // Calculate new selection at end of pasted content
      let lastPastedBlockIndex = -1;
      for (let i = 0; i < updatedBlocks.length; i++) {
        if (
          updatedBlocks[i].text === textBeforeCursor &&
          i + pastedContent.blocks.length < updatedBlocks.length
        ) {
          // Point to the last block of pasted content
          lastPastedBlockIndex = i + pastedContent.blocks.length;
          break;
        }
      }

      // If we didn't find the split point, default to last block
      if (lastPastedBlockIndex === -1) {
        lastPastedBlockIndex = updatedBlocks.length - 1;
      }

      const lastPastedBlock = updatedBlocks[lastPastedBlockIndex];

      const newSelection = new SelectionState({
        anchorKey: lastPastedBlock.key,
        anchorOffset: lastPastedBlock.text.length,
        focusKey: lastPastedBlock.key,
        focusOffset: lastPastedBlock.text.length,
      });

      // Create new editor state with the pasted content and decorator
      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        'insert-fragment',
      );
      // Force the selection to be at the end of pasted content
      const editorStateWithSelection = EditorState.forceSelection(
        newEditorState,
        newSelection,
      );

      // Ensure the editor state is updated by creating a new state with the decorator
      const finalEditorState = EditorState.set(editorStateWithSelection, {
        decorator,
      });

      setEditorState(finalEditorState);
    } catch (err: any) {
      setErrorMsg('Pasted content does not seem correct');
    }
  };

  const handleClearError = () => {
    setErrorMsg('');
  };

  const [variableMenuType, setVariableMenuType] = React.useState<any>(null);

  const mappedJobVariables = jobResultVariables?.map((v: JobVariable) => ({
    key: v.value,
    label: v.label,
  }));

  const copyPasteHandlers = enableDraftJSONCopy
    ? {
        onKeyDown: handleKeyDown,
        onPasteCapture: handlePaste,
      }
    : {};

  const INLINE_STYLES = [
    {label: t('Bold', 'Bold'), style: 'BOLD', icon: 'bold'},
    {label: t('Italic', 'Italic'), style: 'ITALIC', icon: 'italic'},
    {label: t('Underline', 'Underline'), style: 'UNDERLINE', icon: 'underline'},
  ];

  const BLOCK_TYPES = [
    {label: t('Header_1', 'Header 1'), style: 'header-one', icon: 'h1'},
    {label: t('Header_2', 'Header 2'), style: 'header-two', icon: 'h2'},
    {
      label: t('Bulleted_list', 'Bulleted list'),
      style: 'unordered-list-item',
      icon: 'list-ul',
    },
    {
      label: t('Numbered_list', 'Numbered list'),
      style: 'ordered-list-item',
      icon: 'list-ol',
    },
    {label: t('Quote', 'Quote'), style: 'blockquote', icon: 'quote-left'},
  ];

  return (
    <>
      <div
        className={classify(css.root, classNames?.wrapper, {
          [css.genesisEnabled]: isGenesis,
        })}
      >
        {showShroud && (
          <div
            className={css.shroud}
            onClick={
              attachingFile ? toggleShowAttachFile : toggleShowAddHyperlink
            }
          />
        )}
        {attachingFile && (
          <div className={css.file}>
            <SubTitleLarge color="secondary">
              {t('Upload_File', 'Upload File')}
            </SubTitleLarge>
            <SubTitleMedium color="tertiary">
              {file ? file.name : t('No_file_Chosen', 'No file Chosen')}
            </SubTitleMedium>
            <div className={css.fileUploadButtons}>
              <label className={css.fileUploadButton}>
                <Button
                  // @ts-expect-error - TS2339 - Property 'click' does not exist on type '{}'.
                  onClick={() => fileRef.current?.click()}
                  size="medium"
                  type={file ? 'secondary' : 'primary'}
                  actionType="reset"
                >
                  {t('Choose_File', 'Choose File')}
                </Button>
                <input
                  type="file"
                  // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
                  ref={fileRef}
                  className={css.fileUploadInput}
                  accept="image/*,.pdf" //add ,.doc,.docx ?
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      setFile(e.target.files[0]);
                    }
                  }}
                />
              </label>
              {file && (
                <Button
                  onClick={handleCompleteSaveFile}
                  size="medium"
                  type="primary"
                  actionType="reset"
                >
                  {t('Save', 'Save')}
                </Button>
              )}
            </div>
          </div>
        )}

        {addingLink && includeHyperlink && (
          <div className={css.link}>
            <SubTitleSmall color="secondary">
              {t('Text_to_display', 'Text to display')}
            </SubTitleSmall>

            <Input
              onChange={(e) => {
                setLink({url: link?.url, label: e.target.value});
              }}
              value={link?.label}
              placeholder={t('Insert_text', 'Insert text')}
              size="small"
              type="text"
            />
            <Input
              onChange={(e) => {
                setLink({label: link?.label, url: e.target.value});
              }}
              value={link?.url}
              placeholder={t('Enter_URL', 'Enter URL')}
              size="small"
              type="text"
            />
            <div className={css.fileUploadButtons}>
              <Button
                onClick={handleAddHyperlink}
                size="small"
                type="primary"
                actionType="reset"
                disabled={!link}
              >
                {t('Save', 'Save')}
              </Button>
            </div>
          </div>
        )}

        {addingLinkV2 && includeHyperlinkV2 && (
          <Modal isOpen={addingLinkV2} onClose={handleResetLink} size={'large'}>
            <ModalHeader onCloseButtonClick={handleResetLink}>
              <SubTitleMedium>Add Link</SubTitleMedium>
            </ModalHeader>
            <ModalBody className={css.linkModal}>
              <Input
                type="text"
                label="Text"
                placeholder="Link text"
                required
                onChange={(e) => {
                  handleSetLinkV2(
                    linkV2.url,
                    e.target.value,
                    attachChatbotCustomLink,
                  );
                }}
                value={linkV2.label}
              />
              <Input
                type="url"
                label="URL"
                placeholder="www.exampleurl.com"
                required
                onChange={(e) => {
                  handleSetLinkV2(
                    e.target.value,
                    linkV2.label,
                    attachChatbotCustomLink,
                  );
                }}
                value={linkV2.url}
              />
              {chatbotCustomLinkGenerationEnabled && (
                <>
                  <Toggle
                    labelPosition="right"
                    name="dynamic-link-toggle"
                    checked={attachChatbotCustomLink}
                    onChange={toggleAttachChatbotCustomLink}
                  >
                    Generate Dynamic Link
                  </Toggle>
                  <BodySmall color="tertiary">
                    Candidate info appends to a base URL, creating a dynamic
                    link that redirects to pre-filled job applications. Example
                    of dynamic link, www.acme.com?conv_id=12345
                  </BodySmall>
                  {attachChatbotCustomLink && (
                    <Button
                      iconLeftName="copy"
                      type="tertiary"
                      isFluid={false}
                      classNames={{
                        wrapper: css.copyAPIBtn,
                      }}
                      onClick={handleCopyAPIEndpoint}
                    >
                      Copy API endpoint
                    </Button>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="ghost" onClick={handleResetLink}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleAddHyperlinkV2}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        )}

        <div
          className={classify(
            css.scroll,
            isOneLine && css.oneLine,
            classNames?.editor,
          )}
          // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
          ref={rootRef}
          style={isOneLine ? {} : {height}}
          // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
          onClick={() => editorRef.current?.focus()}
        >
          <div className={css.text} {...copyPasteHandlers}>
            <div className={css.content}>
              {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2786 - 'Editor' cannot be used as a JSX component. */}
              <Editor
                blockStyleFn={getBlockStyle}
                blockRendererFn={(contentBlock) =>
                  blockRenderer(contentBlock, handleChange, editorState)
                }
                ref={editorRef}
                editorState={editorState}
                onChange={handleChange}
                spellCheck
                onTab={handleTab}
                handleKeyCommand={handleKeyCommand}
                readOnly={readOnly}
                placeholder={placeholder}
                onBlur={onBlur ? onBlur : undefined}
              />
            </div>
          </div>
        </div>

        {!readOnly && (
          <div className={css.editorFooter}>
            {includeFileAttachement && Number(files?.length) > 0 && (
              <div className={css.fileAttachmentContiner}>
                <div>{`${t('Attachments', 'Attachments')} (${
                  files?.length ? files.length : ''
                }):`}</div>
                {files?.map(({name, handleDownload, handleRemove}) => {
                  return (
                    <div key={name} className={css.fileAttachmentItem}>
                      <Link
                        as="bodyMedium"
                        color="information"
                        underline={false}
                        onClick={handleDownload}
                      >
                        {`<${name}>`}
                      </Link>
                      {handleRemove && (
                        <Icon
                          color="secondary"
                          name="times"
                          onClick={handleRemove}
                          size="small"
                          type="regular"
                          className={css.removeFileIcon}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {
              <div
                className={classify(css.editorControlBox, {
                  [css.editorControlBoxCondensed]: !includeFormatting,
                })}
              >
                {jobResultVariables && (
                  <ClickAway>
                    {({isOpen, onOpen, clickAway, triggerRef, boundaryRef}) => (
                      <>
                        <Icon
                          color="secondary"
                          name="brackets-curly"
                          onClick={onOpen}
                          size="medium"
                          type="regular"
                          // @ts-expect-error - TS2769 - No overload matches this call.
                          ref={triggerRef}
                        />

                        {isOpen && (
                          <Menu
                            onSelect={(option) => {
                              handleSelectJobVariable(option);
                              clickAway();
                            }}
                            options={mappedJobVariables}
                            size="small"
                            classNames={{wrapper: css.variableListWrapper}}
                            // @ts-expect-error - TS2769 - No overload matches this call.
                            ref={boundaryRef}
                          />
                        )}
                      </>
                    )}
                  </ClickAway>
                )}

                {templateVariables && (
                  <ClickAway>
                    {({isOpen, onOpen, clickAway, triggerRef, boundaryRef}) => (
                      <>
                        <Icon
                          color="secondary"
                          name="brackets-curly"
                          onClick={onOpen}
                          size="medium"
                          type="regular"
                          // @ts-expect-error - TS2769 - No overload matches this call.
                          ref={triggerRef}
                        />

                        {isOpen && (
                          <Menu
                            onSelect={(option) => {
                              handleSelectTemplateVariable(option);
                              clickAway();
                            }}
                            options={templateVariables}
                            size="small"
                            classNames={{wrapper: css.variableListWrapper}}
                            // @ts-expect-error - TS2769 - No overload matches this call.
                            ref={boundaryRef}
                          />
                        )}
                      </>
                    )}
                  </ClickAway>
                )}

                {nodeVariables && (
                  <ClickAway onChange={() => setVariableMenuType(null)}>
                    {({isOpen, onOpen, clickAway, triggerRef, boundaryRef}) => (
                      <>
                        <Tooltip
                          body={t('Node_variables', 'Node variables')}
                          classNames={{tooltip: css.tooltipWrapper}}
                        >
                          {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                          <Icon
                            color="secondary"
                            name="diagram-next"
                            onClick={onOpen}
                            size="medium"
                            type="light"
                            ref={triggerRef}
                          />
                        </Tooltip>

                        {isOpen && (
                          <Menu
                            onSelect={(option) => {
                              handleSelectNodeVariable(option);
                              clickAway();
                            }}
                            options={nodeVariables}
                            size="small"
                            classNames={{wrapper: css.variableListWrapper}}
                            // @ts-expect-error - TS2769 - No overload matches this call.
                            ref={boundaryRef}
                          />
                        )}
                      </>
                    )}
                  </ClickAway>
                )}

                {atsVariables && (
                  <Tooltip
                    body={atsTooltip}
                    classNames={{tooltip: css.tooltipWrapper}}
                  >
                    <Icon
                      color="secondary"
                      name="brackets-curly"
                      onClick={handleSelectAtsVariable}
                      size="medium"
                      type="regular"
                    />
                  </Tooltip>
                )}

                {/* file upload control */}
                {includeFileAttachement && (
                  <Tooltip
                    body={t('Attachments', 'Attachments')}
                    classNames={{tooltip: css.tooltipWrapper}}
                  >
                    <Icon
                      color="secondary"
                      name="paperclip"
                      onClick={toggleShowAttachFile}
                      size="medium"
                      type="regular"
                    />
                  </Tooltip>
                )}

                {includeFormatting && (
                  <>
                    {INLINE_STYLES.map(({label, style, icon}) => (
                      <Tooltip
                        body={label}
                        key={label}
                        classNames={{tooltip: css.tooltipWrapper}}
                      >
                        <Icon
                          color="secondary"
                          name={icon}
                          onClick={() =>
                            handleChange(
                              RichUtils.toggleInlineStyle(editorState, style),
                            )
                          }
                          size="medium"
                          type="regular"
                        />
                      </Tooltip>
                    ))}

                    {/* Alignment controls */}

                    {BLOCK_TYPES.map(({label, style, icon}) => (
                      <Tooltip
                        body={label}
                        key={label}
                        classNames={{tooltip: css.tooltipWrapper}}
                      >
                        <Icon
                          color="secondary"
                          name={icon}
                          onClick={() =>
                            handleChange(
                              RichUtils.toggleBlockType(editorState, style),
                            )
                          }
                          size="medium"
                          type="regular"
                        />
                      </Tooltip>
                    ))}
                  </>
                )}

                {includeHyperlink && (
                  <Tooltip
                    body={t('Hyperlink', 'Hyperlink')}
                    classNames={{tooltip: css.tooltipWrapper}}
                  >
                    <Icon
                      color="secondary"
                      name="link"
                      onClick={toggleShowAddHyperlink}
                      size="medium"
                      type="regular"
                    />
                  </Tooltip>
                )}

                {includeHyperlinkV2 && (
                  <Tooltip
                    body={'Hyperlink'}
                    classNames={{tooltip: css.tooltipWrapper}}
                  >
                    <Icon
                      color="secondary"
                      name="link"
                      onClick={toggleShowAddHyperlinkV2}
                      size="medium"
                      type="regular"
                    />
                  </Tooltip>
                )}

                {/* emoji control */}
                {includeEmoji && (
                  <Icon
                    color="secondary"
                    name="face-smile"
                    onClick={handleEmojiButtonClick}
                    size="medium"
                    type="regular"
                  />
                )}

                {includeGif && (
                  <strong onClick={handleGifButtonClick}>
                    {t('gif', 'gif')}
                  </strong>
                )}

                {includeChatbotLink && (
                  // @ts-expect-error - TS2769 - No overload matches this call.
                  <SubTitleSmall onClick={handleAddChatbotLink}>
                    {t('Chatbot_Link', 'Chatbot Link')}
                  </SubTitleSmall>
                )}
              </div>
            }
          </div>
        )}
      </div>
      {errorMsg && (
        <div className={css.errorContainer}>
          <InContextAlert
            leftIconName="shield-exclamation"
            onCloseClick={handleClearError}
            semantic="danger"
            dismissable={true}
          >
            {errorMsg}
          </InContextAlert>
        </div>
      )}
    </>
  );
}

// Previews with variables inserted
export function RichEditorPreview({
  defaultValue,
  variablePreviewValues,
}: {
  defaultValue: RawDraftContentState;
  variablePreviewValues: VariablePreviewValue;
}) {
  const decorator = buildPreviewDecorator({variablePreviewValues});

  const editorState = !isEmpty(defaultValue)
    ? EditorState.createWithContent(convertFromRaw(defaultValue), decorator)
    : EditorState.createEmpty(decorator);
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();

  return (
    // @ts-expect-error - TS2786 - 'Editor' cannot be used as a JSX component.
    <Editor
      blockStyleFn={getBlockStyle}
      // @ts-expect-error - TS2769 - No overload matches this call.
      ref={editorRef}
      editorState={editorState}
      onChange={() => {}}
      readOnly
    />
  );
}

// @ts-expect-error TS2304
function getBlockStyle(block: BlockNodeRecord) {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return block.getType();
  }
}
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
} as const;
export const buildDecorator = (
  props: Readonly<{
    dynamicLabels?: DynamicLabels;
    showAddAttachmentLink?: AttachmentLinkMeta;
    onPlaceholderReplace: () => void;
    linkTarget?: string;
    chatbotCustomLinkGenerationEnabled: boolean;
    jobDynamicLabels?: DynamicLabels;
    handleRemoveLink?: () => void;
    handleEditLink?: () => void;
  }>,
): CompositeDecorator => {
  const {
    linkTarget,
    handleRemoveLink,
    handleEditLink,
    chatbotCustomLinkGenerationEnabled,
  } = props;
  let decorators = [
    LinkDecoratorEntry(linkTarget),
    dynamicLabelDecorator(props.dynamicLabels ?? []),
    PlaceholderDecorator(props.onPlaceholderReplace),
    templateVariableDecorator,
    nodeVariableDecorator,
    {
      ...hyperlinkDecorator,
      props: {
        handleRemoveLink,
        handleEditLink,
        chatbotCustomLinkGenerationEnabled,
      },
    },
  ];

  if (props.jobDynamicLabels && props.jobDynamicLabels.length) {
    decorators = [...decorators, dynamicLabelDecorator(props.jobDynamicLabels)];
  }

  if (props.showAddAttachmentLink) {
    decorators = [
      ...decorators,
      AttachmentLinkDecorator(props.showAddAttachmentLink.pattern),
    ];
  }

  return new CompositeDecorator(decorators);
};

export const buildPreviewDecorator = ({
  variablePreviewValues,
}: {
  variablePreviewValues: VariablePreviewValue;
}): CompositeDecorator => {
  const decorators = [
    LinkDecoratorEntry('_blank'),
    {...variablePreviewDecorator, props: {variablePreviewValues}},
  ];

  return new CompositeDecorator(decorators);
};

const VariablePreviewComponent = ({
  children,
  className,
  contentState,
  entityKey,
  blockKey,
  start,
  end,
  variablePreviewValues,
  block,
}: {
  className?: string;
  children: React.ReactNode;
  contentState: ContentState;
  entityKey?: string;
  blockKey: string;
  start: number;
  end: number;
  variablePreviewValues: VariablePreviewValue;
  block: ContentBlock;
}) => {
  // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'DraftEntityInstance'.
  const entity = entityKey ? contentState.getEntity(entityKey).data : null;

  const contentBlock = contentState.getBlockForKey(blockKey);

  // Define styles for different block types
  const blockStyles = {
    'header-one': {fontSize: '24px', fontWeight: 'bold'},
    'header-two': {fontSize: '20px', fontWeight: 'bold'},

    blockquote: {
      fontStyle: 'italic',
      borderLeft: '2px solid #ccc',
      paddingLeft: '8px',
    },
    'unordered-list-item': {listStyleType: 'disc', fontWeight: 'bold'},
    'ordered-list-item': {listStyleType: 'decimal', fontWeight: 'bold'},
    unstyled: {
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
    },
  } as const;

  const fontStyles = contentBlock.getInlineStyleAt(0).toJS();
  const style = {
    fontWeight: fontStyles.includes('BOLD') ? 'bold' : 'normal',
    fontStyle: fontStyles.includes('ITALIC') ? 'italic' : 'normal',
    textDecoration: fontStyles.includes('UNDERLINE') ? 'underline' : 'none',
  } as const;

  if (!entity) {
    return null;
  }
  return (
    <span className={className} style={style}>
      {variablePreviewValues[entity.job_attribute]}
    </span>
  );
};

export const variablePreviewDecorator = {
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentState,
  ) => {
    contentBlock.findEntityRanges((meta) => {
      const entityKey = meta.getEntity();
      if (!entityKey) {
        return false;
      }
      const entityType = contentState.getEntity(entityKey)?.getType();
      return entityType === 'JOB_RESULT_ATTRIBUTE';
    }, callback);
  },
  component: VariablePreviewComponent,
} as const;

// @ts-expect-error - TS7030 - Not all code paths return a value.
function blockRenderer(
  // @ts-expect-error TS2304
  contentBlock: BlockNodeRecord,
  onChange: (newEditorState: EditorState | any) => void,
  // @ts-expect-error - TS7006 - Parameter 'editorState' implicitly has an 'any' type.
  editorState,
) {
  const type = contentBlock.getType();
  if (type === 'atomic') {
    return {
      component: GifBlock,
      editable: false,
      props: {onChange, editorState},
    };
  }
}

const {hasCommandModifier} = KeyBindingUtil;
